import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, FileText, User, Building2, Calendar, Clock, ChevronRight, Loader2, Eye, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OnboardingSessionsPage() {
  const [sessions, setSessions] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const fetchSessions = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/onboarding-sessions`, { withCredentials: true });
    setSessions(data);
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const deleteSession = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Ta bort denna onboarding-session?")) return;
    try {
      await axios.delete(`${API}/admin/onboarding-sessions/${id}`, { withCredentials: true });
      fetchSessions();
    } catch {}
  };

  const filtered = sessions.filter(s => {
    if (filter === "completed" && !s.completed) return false;
    if (filter === "in_progress" && s.completed) return false;
    if (search) {
      const q = search.toLowerCase();
      return s.company_name?.toLowerCase().includes(q) || s.contact_name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div data-testid="onboarding-sessions-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">Onboarding Sessions</h1>
          <p className="text-sm text-[#857973]">{sessions.length} sessions &middot; {sessions.filter(s => s.completed).length} completed</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#857973]" />
          <input data-testid="onboarding-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by company, contact or email..."
            className="w-full bg-[#0F1F3A] border border-[#1E3050] text-white pl-10 pr-4 py-2.5 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
        </div>
        <div className="flex gap-1">
          {["all", "completed", "in_progress"].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-2 rounded-sm text-xs font-mono transition-colors ${filter === f ? "bg-[#F59073]/10 text-[#F59073] border border-[#F59073]/30" : "bg-[#0F1F3A] text-[#857973] border border-[#1E3050] hover:text-white"}`}>
              {f.replace("_", " ")}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {filtered.map(s => (
          <div key={s.id} onClick={() => navigate(`/admin/onboarding/${s.id}`)} data-testid={`onboarding-card-${s.id}`}
            className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-5 cursor-pointer hover:border-[#1E3050] transition-all group">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${s.completed ? "bg-green-500/10" : "bg-[#F59073]/10"}`}>
                  {s.completed ? <FileText className="w-5 h-5 text-green-400" /> : <Clock className="w-5 h-5 text-[#F59073]" />}
                </div>
                <div>
                  <h3 className="font-outfit font-medium text-white flex items-center gap-2">
                    {s.company_name}
                    <span className={`text-xs px-2 py-0.5 rounded-sm font-mono ${s.completed ? "bg-green-500/10 text-green-400" : "bg-yellow-500/10 text-yellow-400"}`}>
                      {s.completed ? "completed" : `step ${s.current_step || 0}`}
                    </span>
                    {s.ai_brief && <span className="text-xs px-2 py-0.5 rounded-sm font-mono bg-purple-500/10 text-purple-400">AI brief</span>}
                  </h3>
                  <div className="flex items-center gap-4 text-xs text-[#857973] mt-1">
                    <span className="flex items-center gap-1"><User className="w-3 h-3" /> {s.contact_name}</span>
                    <span>{s.email}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <span className="text-xs text-[#857973] block">{s.created_at?.slice(0, 10)}</span>
                  {s.answers?.project_types?.length > 0 && (
                    <div className="flex gap-1 mt-1 justify-end">
                      {s.answers.project_types.slice(0, 3).map(pt => (
                        <span key={pt} className="text-[10px] bg-[#1E3050]/40 text-[#857973] px-1.5 py-0.5 rounded-sm">{pt}</span>
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={(e) => deleteSession(s.id, e)} data-testid={`delete-session-${s.id}`}
                  className="p-1.5 text-[#1E3050] hover:text-red-400 transition-colors" title="Ta bort">
                  <Trash2 className="w-4 h-4" />
                </button>
                <ChevronRight className="w-4 h-4 text-[#1E3050] group-hover:text-[#F59073] transition-colors" />
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-sm text-[#857973] text-center py-12">No onboarding sessions found</p>}
      </div>
    </div>
  );
}
